/**
 * PartnerJoinPage - Partner B joining and profile completion
 *
 * Hardened join flow:
 *  - Defensive URL-param validation up front (no silent failures)
 *  - Idempotent: if Partner B has already accepted, we still progress them
 *  - Friendly fallback for expired / used / revoked invitation links
 *  - Backend error `detail` is surfaced verbatim (no generic "Failed to join")
 *  - Diagnostic console logs (non-sensitive — token values are never logged)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPIRED_COPY =
  'Your invitation link may have expired or already been used. Please request a new link to continue your MarrsingClause® experience.';

// Lightweight diagnostic logger — only fires in non-production builds OR when
// `?debug=1` is appended. Never logs raw token values.
const diag = (...args) => {
  try {
    const isDev = process.env.NODE_ENV !== 'production';
    const debugFlag = typeof window !== 'undefined' && window.location.search.includes('debug=1');
    if (isDev || debugFlag) {
      // eslint-disable-next-line no-console
      console.log('[PartnerJoin]', ...args);
    }
  } catch { /* noop */ }
};

const PartnerJoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    pronouns: '',
  });

  const relationshipId = searchParams.get('relationship_id');
  const userId = searchParams.get('user_id');

  useEffect(() => {
    diag('mount', {
      relationship_id_present: !!relationshipId,
      user_id_present: !!userId,
      has_token_param: searchParams.has('token'),
      api_base: API,
    });
    if (!relationshipId || !userId) {
      setLinkError(EXPIRED_COPY);
      return;
    }
    sessionStorage.setItem('relationship_id', relationshipId);
    sessionStorage.setItem('user_id', userId);
  }, [relationshipId, userId, searchParams]);

  const proceedToDashboard = (selectedLevel) => {
    if (selectedLevel) sessionStorage.setItem('selected_level', selectedLevel);
    toast.success('Welcome! You can now start your reflection.');
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!relationshipId || !userId) {
      setLinkError(EXPIRED_COPY);
      return;
    }
    setLoading(true);

    // 1) Fetch the relationship and locate Partner B
    let relationship = null;
    let partnerB = null;
    try {
      diag('GET relationship', { url: `${API}/relationships/${relationshipId}` });
      const r = await axios.get(`${API}/relationships/${relationshipId}`);
      diag('GET status', r.status);
      relationship = r.data;
      partnerB = relationship?.partners?.find((p) => p.user_id === userId) || null;
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      diag('GET failed', { status, detail });
      setLoading(false);
      if (status === 404) {
        setLinkError(EXPIRED_COPY);
        return;
      }
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'We could not load your invitation. Please try again in a moment.'
      );
      return;
    }

    if (!partnerB) {
      diag('partner B not found in relationship', {
        partners_count: relationship?.partners?.length || 0,
      });
      setLoading(false);
      setLinkError(EXPIRED_COPY);
      return;
    }

    // 2) If the partner has already accepted, skip re-accept and continue.
    //    The endpoint is idempotent on the server side too, but this avoids
    //    a redundant write when a partner returns to the link.
    const alreadyAccepted =
      partnerB.invite_status === 'accepted' || !!partnerB.accepted_at;

    try {
      if (!alreadyAccepted) {
        diag('POST accept', {
          url: `${API}/relationships/${relationshipId}/partner/${userId}/accept`,
        });
        const r = await axios.post(
          `${API}/relationships/${relationshipId}/partner/${userId}/accept`,
          formData
        );
        diag('POST status', r.status);
      } else {
        diag('skip accept (already accepted) — proceeding to dashboard');
      }
      proceedToDashboard(relationship?.relationship?.selected_level);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      diag('POST failed', { status, detail });
      setLoading(false);

      if (status === 404 || status === 410) {
        setLinkError(EXPIRED_COPY);
        return;
      }

      toast.error(
        typeof detail === 'string' && detail.length > 0
          ? detail
          : "We couldn't complete your join right now. Please try again in a moment."
      );
    }
  };

  // ── Friendly fallback when the invitation link is expired / malformed ──
  if (linkError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="px-6 py-12">
          <div
            className="mx-auto max-w-xl rounded-2xl border border-stone-200 bg-white p-8 text-center shadow-sm"
            data-testid="partner-join-link-error"
          >
            <AlertCircle className="mx-auto h-8 w-8 text-stone-500" />
            <h2 className="mt-4 text-2xl font-semibold text-stone-900">
              We couldn't open your invitation
            </h2>
            <p className="mt-3 text-sm leading-7 text-stone-600">{linkError}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-mc-blue-deep px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark"
              data-testid="partner-join-error-home-btn"
            >
              Back to MarrsingClause®
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="px-6 py-12">
        <div className="mx-auto max-w-2xl">
          <h2 className="mb-2 text-4xl font-bold text-stone-900">Welcome, Partner B</h2>
          <p className="mb-12 text-stone-600">
            Complete your profile to begin your MarrsingClause® reflection.
          </p>

          <form
            onSubmit={handleSubmit}
            className="space-y-6 rounded-2xl border border-stone-100 bg-white p-8 shadow-sm"
            data-testid="partner-join-form"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-stone-700">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="h-14 w-full rounded-lg border-stone-200 bg-stone-50 px-4 text-lg transition-all focus:border-stone-900 focus:ring-0"
                  data-testid="partner-first-name-input"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-stone-700">Last Name *</label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="h-14 w-full rounded-lg border-stone-200 bg-stone-50 px-4 text-lg transition-all focus:border-stone-900 focus:ring-0"
                  data-testid="partner-last-name-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-stone-700">Preferred Name (Optional)</label>
                <input
                  type="text"
                  value={formData.preferred_name}
                  onChange={(e) => setFormData({ ...formData, preferred_name: e.target.value })}
                  className="h-14 w-full rounded-lg border-stone-200 bg-stone-50 px-4 text-lg transition-all focus:border-stone-900 focus:ring-0"
                  data-testid="partner-preferred-name-input"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-stone-700">Pronouns (Optional)</label>
                <input
                  type="text"
                  value={formData.pronouns}
                  onChange={(e) => setFormData({ ...formData, pronouns: e.target.value })}
                  className="h-14 w-full rounded-lg border-stone-200 bg-stone-50 px-4 text-lg transition-all focus:border-stone-900 focus:ring-0"
                  placeholder="e.g., she/her, he/him, they/them"
                  data-testid="partner-pronouns-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-mc-blue-deep px-8 py-4 text-lg font-medium text-white transition-all hover:scale-[1.02] hover:bg-mc-blue-dark active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="partner-join-continue-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Joining…
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerJoinPage;
